import {
  BRAND,
  LIMITS,
  SETTINGS_LIMITS,
  whatsappShareUrl,
  type AdminListItem,
  type AdminsResponse,
  type ErrorLogItem,
  type OwnerHealthResponse,
  type SettingsResponse,
  type SystemSettings,
} from '@crew/shared';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { CopyIcon, EditIcon, PlusIcon, RefreshIcon, TrashIcon, WhatsAppIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Notice } from '@/components/ui/Notice';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/Toast';
import { demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';
import { formatWhen } from '@/lib/activity';
import { copyText } from '@/lib/clipboard';
import { ApiClientError, errorMessage } from '@/services/apiError';
import { ownerApi } from '@/services/ownerApi';

/**
 * מסך המפתח — רק למשתמש שמוגדר כ-OWNER_UID בשרת.
 * גם אם מישהו אחר יגיע לכתובת הזו, השרת יחסום כל פעולה כאן.
 */

type Tab = 'admins' | 'settings' | 'health' | 'errors';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'admins', label: 'מנהלים' },
  { id: 'settings', label: 'הגדרות מערכת' },
  { id: 'health', label: 'בריאות' },
  { id: 'errors', label: 'יומן תקלות' },
];

type Load<T> = { state: 'loading' } | { state: 'ready'; data: T } | { state: 'error'; message: string };

/** טעינה עם רענון ידני; במצב תצוגה מתעדכן גם כשהנתונים המקומיים משתנים */
function useLoad<T>(fn: () => Promise<T>): [Load<T>, () => void] {
  const [state, setState] = useState<Load<T>>({ state: 'loading' });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let cancelled = false;
    fn()
      .then((data) => !cancelled && setState({ state: 'ready', data }))
      .catch((err) => !cancelled && setState({ state: 'error', message: errorMessage(err) }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);
  useEffect(() => (IS_DEMO ? demoStore.subscribe(reload) : undefined), [reload]);
  return [state, reload];
}

export function DevPage() {
  const { me } = useAdmin();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'admins') as Tab;
  if (me.role !== 'owner') return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="מסך מפתח" subtitle="רק אתה רואה את המסך הזה. השרת מאשר כל פעולה כאן מול ה-UID שלך." />
      <QuietCard />
      <div role="tablist" className="-mx-1 mb-4 flex gap-1 overflow-x-auto px-1 pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            type="button"
            onClick={() => setParams(t.id === 'admins' ? {} : { tab: t.id }, { replace: true })}
            className={`min-h-10 shrink-0 rounded-full px-4 text-[14px] font-semibold ${tab === t.id ? 'bg-brand text-white' : 'bg-sunken text-ink-2 hover:text-ink'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'admins' && <AdminsTab />}
      {tab === 'settings' && <SettingsTab />}
      {tab === 'health' && <HealthTab />}
      {tab === 'errors' && <ErrorsTab />}
    </div>
  );
}

// ── רכיבים קטנים ──────────────────────────────────────────────

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="mb-5 rounded-card border border-line bg-surface p-4 md:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[17px] font-bold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Switch({ checked, onChange, label, danger, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; danger?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${checked ? (danger ? 'bg-danger' : 'bg-ok') : 'bg-line-strong'}`}
    >
      <span className={`absolute top-0.5 size-6 rounded-full bg-white shadow transition-all ${checked ? 'start-[22px]' : 'start-0.5'}`} />
    </button>
  );
}

function Centered() {
  return (
    <div className="flex justify-center py-10">
      <Spinner />
    </div>
  );
}

// ── מצב שקט ───────────────────────────────────────────────────

function QuietCard() {
  const { me, user } = useAdmin();
  const toast = useToast();
  const [quiet, setQuiet] = useState<boolean>(IS_DEMO ? demoStore.quietMode() : me.quietMode);
  const [busy, setBusy] = useState(false);
  async function toggle(v: boolean) {
    setBusy(true);
    try {
      await ownerApi(user).setQuiet(v);
      setQuiet(v);
      toast(v ? 'מצב שקט הופעל' : 'מצב שקט כובה');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={`mb-5 flex items-start gap-3 rounded-card border p-4 ${quiet ? 'border-brand/30 bg-brand-soft' : 'border-line bg-surface'}`}>
      <div className="min-w-0 flex-1">
        <div className="text-[16px] font-bold">מצב שקט {quiet && <span className="text-[13px] font-semibold text-brand">· פעיל</span>}</div>
        <p className="mt-0.5 text-[13px] text-ink-2">
          לא תקבל התראות Push, ופעולות שלך לא ישלחו Push למנהלים, כדי שתוכל לבדוק בלי להפריע. הפעולות עדיין נרשמות ביומן הפעילות ובמרכז ההתראות.
        </p>
      </div>
      {busy ? <Spinner size={22} /> : <Switch checked={quiet} onChange={toggle} label="מצב שקט" />}
    </div>
  );
}

// ── מנהלים ────────────────────────────────────────────────────

function AdminsTab() {
  const { user } = useAdmin();
  const [list, reload] = useLoad<AdminsResponse>(() => ownerApi(user).admins());
  const [adding, setAdding] = useState(false);
  const [link, setLink] = useState<{ name: string; email: string; link: string } | null>(null);

  const activeCount = list.state === 'ready' ? list.data.admins.filter((a) => a.active).length : 0;
  const max = list.state === 'ready' ? list.data.max : 4;

  return (
    <>
      <Section
        title={`מנהלים${list.state === 'ready' ? ` · ${activeCount}/${max} פעילים` : ''}`}
        aside={
          <div className="flex gap-2">
            <Button variant="secondary" className="min-h-10 px-3 text-[14px]" icon={<RefreshIcon size={16} />} onClick={reload}>
              רענון
            </Button>
            <Button className="min-h-10 px-3 text-[14px]" icon={<PlusIcon size={17} />} disabled={activeCount >= max} onClick={() => setAdding(true)}>
              הוספה
            </Button>
          </div>
        }
      >
        {activeCount >= max && list.state === 'ready' && (
          <div className="mb-3">
            <Notice tone="info">הגעת למקסימום של {max} מנהלים פעילים. אפשר להגדיל את המספר בלשונית "הגדרות מערכת", או להשבית/להסיר מנהל.</Notice>
          </div>
        )}
        {list.state === 'loading' && <Centered />}
        {list.state === 'error' && <Notice tone="error">{list.message}</Notice>}
        {list.state === 'ready' && (
          <ul className="space-y-3">
            {list.data.admins.map((a) => (
              <AdminRow key={a.uid} a={a} onChanged={reload} onLink={(l) => setLink({ name: a.displayName, email: a.email, link: l })} />
            ))}
          </ul>
        )}
      </Section>
      <p className="mb-6 text-[13px] text-ink-3">
        מנהל מושבת לא יכול להיכנס, אבל נשאר ברשימה וקל להפעיל אותו מחדש. מנהל שהוסר יורד מהרשימה. השם שלו נשאר ביומן הפעילות, ואפשר להוסיף אותו שוב לפי האימייל.
      </p>
      <AddAdminDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(name, email, l) => {
          setAdding(false);
          reload();
          if (l) setLink({ name, email, link: l });
        }}
      />
      <PasswordLinkDialog value={link} onClose={() => setLink(null)} />
    </>
  );
}

function AdminRow({ a, onChanged, onLink }: { a: AdminListItem; onChanged: () => void; onLink: (link: string) => void }) {
  const { user } = useAdmin();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(a.displayName);
  const owner = a.role === 'owner';

  async function run(key: string, fn: () => Promise<unknown>, done?: string) {
    setBusy(key);
    try {
      await fn();
      if (done) toast(done);
      onChanged();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  const api = ownerApi(user);

  return (
    <li className={`rounded-control border p-3 ${a.active ? 'border-line' : 'border-dashed border-line-strong bg-paper'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[16px] font-bold">{a.displayName}</span>
        {owner && <span className="rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-white">מפתח</span>}
        {!a.active && <span className="rounded-full bg-sunken px-2 py-0.5 text-[11px] font-semibold text-ink-2">מושבת</span>}
        {a.hasPassword === false && <span className="rounded-full bg-warn-soft px-2 py-0.5 text-[11px] font-semibold text-warn">עוד לא בחר סיסמה</span>}
      </div>
      <div className="ltr mt-0.5 text-start text-[13px] text-ink-2">{a.email}</div>
      <div className="mt-1 text-[12px] text-ink-3">
        התחברות אחרונה: {a.lastLoginAt ? formatWhen(new Date(a.lastLoginAt)) : 'לא ידוע'} · מכשירים להתראות: {a.pushDevices}
      </div>

      {renaming ? (
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <TextField label="שם תצוגה" value={name} onChange={(e) => setName(e.target.value)} maxLength={LIMITS.maxNameLength} />
          </div>
          <Button
            loading={busy === 'rename'}
            onClick={() =>
              run('rename', async () => {
                await api.adminAction(a.uid, { action: 'rename', displayName: name });
                setRenaming(false);
              }, 'השם עודכן')
            }
          >
            שמירה
          </Button>
          <Button variant="secondary" onClick={() => setRenaming(false)}>
            ביטול
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <SmallBtn onClick={() => setRenaming(true)} icon={<EditIcon size={15} />}>
            שינוי שם
          </SmallBtn>
          <SmallBtn
            loading={busy === 'link'}
            onClick={() =>
              run('link', async () => {
                const r = await api.adminAction(a.uid, { action: 'password_link' });
                if ('passwordLink' in r) onLink(r.passwordLink);
              })
            }
          >
            קישור לבחירת סיסמה
          </SmallBtn>
          {!owner &&
            (a.active ? (
              <SmallBtn loading={busy === 'disable'} onClick={() => run('disable', () => api.adminAction(a.uid, { action: 'disable' }), `${a.displayName} הושבת`)}>
                השבתה
              </SmallBtn>
            ) : (
              <SmallBtn loading={busy === 'enable'} onClick={() => run('enable', () => api.adminAction(a.uid, { action: 'enable' }), `${a.displayName} הופעל מחדש`)}>
                הפעלה מחדש
              </SmallBtn>
            ))}
          {!owner && (
            <SmallBtn danger icon={<TrashIcon size={15} />} onClick={() => setConfirmRemove(true)}>
              הסרה
            </SmallBtn>
          )}
        </div>
      )}
      {confirmRemove && (
        <div className="mt-3 rounded-control bg-danger-soft p-3">
          <p className="text-[14px] font-semibold text-danger">להסיר את {a.displayName}? הוא לא יוכל להיכנס יותר, ויירד מהרשימה.</p>
          <div className="mt-2 flex gap-2">
            <Button variant="danger" loading={busy === 'remove'} onClick={() => run('remove', () => api.adminAction(a.uid, { action: 'remove' }), `${a.displayName} הוסר`)}>
              הסרה
            </Button>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)}>
              ביטול
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function SmallBtn({ children, onClick, icon, danger, loading }: { children: ReactNode; onClick: () => void; icon?: ReactNode; danger?: boolean; loading?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`inline-flex min-h-9 items-center gap-1.5 rounded-control border px-3 text-[13px] font-semibold disabled:opacity-60 ${
        danger ? 'border-danger/30 text-danger hover:bg-danger-soft' : 'border-line-strong text-ink hover:bg-sunken'
      }`}
    >
      {loading ? <Spinner size={14} /> : icon}
      {children}
    </button>
  );
}

function AddAdminDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (name: string, email: string, link: string | null) => void }) {
  const { user } = useAdmin();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setEmail('');
      setName('');
      setErrors({});
      setFormError(null);
    }
  }

  async function save() {
    setBusy(true);
    setErrors({});
    setFormError(null);
    try {
      const r = await ownerApi(user).addAdmin({ email, displayName: name });
      toast(r.existingUser ? 'המנהל נוסף (המשתמש כבר היה קיים ב-Firebase)' : 'המנהל נוסף');
      onAdded(name.trim(), email.trim().toLowerCase(), r.passwordLink);
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) setErrors(err.fields);
      else setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="הוספת מנהל"
      footer={
        <div className="flex gap-2">
          <Button block loading={busy} onClick={save}>
            הוספה
          </Button>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <TextField label="שם מלא" value={name} onChange={(e) => setName(e.target.value)} error={errors.displayName} maxLength={LIMITS.maxNameLength} />
        <TextField label="אימייל (שם המשתמש בכניסה)" type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} autoComplete="off" />
        <p className="text-[13px] text-ink-2">
          אחרי ההוספה תקבל קישור חד-פעמי לבחירת סיסמה. שלח אותו למנהל באופן אישי, למשל בוואטסאפ. כדאי שהאימייל יהיה אמיתי, כדי ש"שכחתי סיסמה" יעבוד.
        </p>
        {formError && <Notice tone="error">{formError}</Notice>}
      </div>
    </Dialog>
  );
}

function PasswordLinkDialog({ value, onClose }: { value: { name: string; email: string; link: string } | null; onClose: () => void }) {
  const toast = useToast();
  if (!value) return null;
  const message = `שלום ${value.name.split(' ')[0]},\nנוספת כמנהל במערכת של ${BRAND.name}.\nבחר/י סיסמה בקישור הזה (עובד פעם אחת):\n${value.link}\n\nאחר כך נכנסים עם האימייל ${value.email} והסיסמה שבחרת.`;
  return (
    <Dialog open onClose={onClose} title={`קישור לבחירת סיסמה — ${value.name}`}>
      <p className="text-[14px] text-ink-2">הקישור עובד פעם אחת ולזמן מוגבל. שלח אותו רק ל{value.name.split(' ')[0]}.</p>
      <div className="ltr mt-3 max-h-28 overflow-y-auto break-all rounded-control bg-sunken p-3 text-start text-[12px]">{value.link}</div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <Button variant="secondary" icon={<CopyIcon size={17} />} onClick={async () => toast((await copyText(message)) ? 'ההודעה הועתקה' : 'ההעתקה נכשלה')}>
          העתקת הודעה
        </Button>
        <a
          href={whatsappShareUrl(message)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-control bg-[#25D366] px-5 text-[15px] font-semibold text-white"
        >
          <WhatsAppIcon size={18} />
          שליחה בוואטסאפ
        </a>
      </div>
      {IS_DEMO && <p className="mt-3 text-[12px] text-ink-3">במצב תצוגה זה קישור לדוגמה ולא עובד.</p>}
    </Dialog>
  );
}

// ── הגדרות מערכת ──────────────────────────────────────────────

function SettingsTab() {
  const { user } = useAdmin();
  const toast = useToast();
  const [loaded, reload] = useLoad<SettingsResponse>(() => ownerApi(user).settings());
  const [draft, setDraft] = useState<SystemSettings | null>(null);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [busy, setBusy] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);

  const base = loaded.state === 'ready' ? loaded.data.settings : null;
  const s = draft ?? base;
  if (loaded.state === 'loading') return <Centered />;
  if (loaded.state === 'error') return <Notice tone="error">{loaded.message}</Notice>;
  if (!s || !base) return null;

  const set = <K extends keyof SystemSettings>(k: K, v: SystemSettings[K]) => setDraft({ ...s, [k]: v });
  const changed = (Object.keys(s) as Array<keyof SystemSettings>).filter((k) => s[k] !== base[k]);

  async function save(patch?: Partial<SystemSettings>) {
    const body = patch ?? Object.fromEntries(changed.map((k) => [k, s![k]]));
    setBusy(true);
    setErrors({});
    try {
      await ownerApi(user).updateSettings(body);
      setDraft(null);
      reload();
      toast('ההגדרות נשמרו. הן נכנסות לתוקף תוך חצי דקה');
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) setErrors(err.fields);
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  const area = (k: 'registrationNotice' | 'personalPageNotice' | 'publicPausedMessage', label: string, hint: string) => (
    <label className="block">
      <span className="mb-1.5 block text-[14px] font-medium text-ink-2">{label}</span>
      <textarea
        value={s[k]}
        onChange={(e) => set(k, e.target.value)}
        rows={3}
        maxLength={SETTINGS_LIMITS.textMax}
        className={`w-full rounded-control border bg-surface px-3 py-2.5 text-[16px] leading-relaxed focus:border-brand focus:outline-none ${errors[k] ? 'border-danger' : 'border-line-strong'}`}
      />
      <span className={`mt-1 block text-[12px] ${errors[k] ? 'text-danger' : 'text-ink-3'}`}>{errors[k] ?? hint}</span>
    </label>
  );

  return (
    <>
      <Section title="עצירת חירום">
        <div className="flex items-start gap-3">
          <div className="flex-1 text-[14px] text-ink-2">
            חוסם זמנית את כל ההרשמות ובקשות השינוי מקבלנים ועובדים, בכל העבודות. המנהלים ממשיכים לעבוד כרגיל. שימושי אם משהו לא תקין ורוצים לעצור לפני שיירשמו נתונים שגויים.
          </div>
          <Switch
            danger
            checked={base.publicPaused}
            disabled={busy}
            label="עצירת הרשמות"
            onChange={(v) => (v ? setConfirmPause(true) : void save({ publicPaused: false }))}
          />
        </div>
        {base.publicPaused && (
          <div className="mt-3">
            <Notice tone="error">ההרשמות עצורות עכשיו. נרשמים רואים: "{base.publicPausedMessage}"</Notice>
          </div>
        )}
        {confirmPause && (
          <div className="mt-3 rounded-control bg-danger-soft p-3">
            <p className="text-[14px] font-semibold text-danger">לעצור את כל ההרשמות עכשיו?</p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="danger"
                loading={busy}
                onClick={async () => {
                  await save({ publicPaused: true });
                  setConfirmPause(false);
                }}
              >
                עצירה
              </Button>
              <Button variant="secondary" onClick={() => setConfirmPause(false)}>
                ביטול
              </Button>
            </div>
          </div>
        )}
      </Section>

      <Section title="כללים">
        <div className="space-y-4">
          <div>
            <NumberStepper
              label="כמה ימים אחרי תאריך העבודה היא עוברת לארכיון"
              value={s.autoArchiveDaysAfter}
              onChange={(v) => set('autoArchiveDaysAfter', v === '' ? 0 : v)}
              min={SETTINGS_LIMITS.autoArchiveDaysAfter.min}
              max={SETTINGS_LIMITS.autoArchiveDaysAfter.max}
              error={errors.autoArchiveDaysAfter ?? null}
            />
            <p className="mt-1 text-[12px] text-ink-3">0 = כבר בלילה שאחרי העבודה. ברירת המחדל: 1.</p>
          </div>
          <div>
            <NumberStepper
              label="מקסימום עובדים שקבלן יכול לדווח בעצמו"
              value={s.maxWorkersPerContractor}
              onChange={(v) => set('maxWorkersPerContractor', v === '' ? 1 : v)}
              min={SETTINGS_LIMITS.maxWorkersPerContractor.min}
              max={SETTINGS_LIMITS.maxWorkersPerContractor.max}
              error={errors.maxWorkersPerContractor ?? null}
            />
            <p className="mt-1 text-[12px] text-ink-3">חל על הרשמה ובקשות שינוי מהקישור. מנהלים יכולים להזין עד {LIMITS.maxWorkers}.</p>
          </div>
          <div>
            <NumberStepper
              label="כמה מנהלים פעילים מותרים (כולל אותך)"
              value={s.maxAdmins}
              onChange={(v) => set('maxAdmins', v === '' ? 1 : v)}
              min={SETTINGS_LIMITS.maxAdmins.min}
              max={SETTINGS_LIMITS.maxAdmins.max}
              error={errors.maxAdmins ?? null}
            />
            <p className="mt-1 text-[12px] text-ink-3">אפשר להגדיל בכל רגע. מנהלים מושבתים לא נספרים. עד {SETTINGS_LIMITS.maxAdmins.max}.</p>
          </div>
        </div>
      </Section>

      <Section title="טקסטים">
        <div className="space-y-4">
          <TextField
            label="חתימה בסוף הודעות הוואטסאפ"
            value={s.whatsappSignature}
            onChange={(e) => set('whatsappSignature', e.target.value)}
            maxLength={SETTINGS_LIMITS.signatureMax}
            error={errors.whatsappSignature}
          />
          {area('registrationNotice', 'הודעה קבועה במסך ההרשמה', 'מוצגת לכל מי שנכנס עם הקוד, בכל העבודות. ריק = בלי הודעה.')}
          {area('personalPageNotice', 'הודעה קבועה בדף האישי של נרשם', 'למשל: "בכל שאלה — 050-0000000". ריק = בלי הודעה.')}
          {area('publicPausedMessage', 'ההודעה שמוצגת בזמן עצירת חירום', 'חובה.')}
        </div>
      </Section>

      <div className="sticky bottom-20 z-10 mb-6 flex items-center gap-3 rounded-card border border-line bg-surface p-3 shadow-sm md:bottom-4">
        <span className="flex-1 text-[13px] text-ink-3">
          {changed.length ? `${changed.length} שינויים שלא נשמרו` : loaded.data.updatedAt ? `עודכן ${formatWhen(new Date(loaded.data.updatedAt))} ע"י ${loaded.data.updatedByName ?? '—'}` : 'ברירות מחדל'}
        </span>
        {changed.length > 0 && (
          <Button variant="secondary" onClick={() => (setDraft(null), setErrors({}))}>
            ביטול
          </Button>
        )}
        <Button loading={busy} disabled={!changed.length} onClick={() => save()}>
          שמירה
        </Button>
      </div>
    </>
  );
}

// ── בריאות המערכת ─────────────────────────────────────────────

function HealthTab() {
  const { user } = useAdmin();
  const [h, reload] = useLoad<OwnerHealthResponse>(() => ownerApi(user).health());
  return (
    <Section
      title="בריאות המערכת"
      aside={
        <Button variant="secondary" className="min-h-10 px-3 text-[14px]" icon={<RefreshIcon size={16} />} onClick={reload}>
          בדיקה חוזרת
        </Button>
      }
    >
      {h.state === 'loading' && <Centered />}
      {h.state === 'error' && <Notice tone="error">השרת לא ענה: {h.message}</Notice>}
      {h.state === 'ready' && (
        <>
          <ul className="divide-y divide-line">
            {h.data.checks.map((c) => (
              <li key={c.key} className="flex items-start gap-3 py-3">
                <span
                  className={`mt-1.5 size-3 shrink-0 rounded-full ${c.ok === true ? 'bg-ok' : c.ok === false ? 'bg-danger' : 'bg-line-strong'}`}
                  aria-label={c.ok === true ? 'תקין' : c.ok === false ? 'תקלה' : 'לא ידוע'}
                />
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold">{c.label}</div>
                  <div className={`text-[13px] ${c.ok === false ? 'text-danger' : 'text-ink-2'}`}>{c.detail}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[
              ['עבודות פעילות', h.data.counts.activeJobs],
              ['בארכיון', h.data.counts.archivedJobs],
              ['במאגר', h.data.counts.contractors],
              ['מנהלים פעילים', h.data.counts.activeAdmins],
              ['תקלות ב-24 שעות', h.data.counts.errorsLast24h],
            ].map(([label, n]) => (
              <div key={label as string} className="rounded-control bg-paper p-3 text-center">
                <div className="tabular text-[22px] font-extrabold">{n}</div>
                <div className="text-[12px] text-ink-3">{label}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[12px] text-ink-3">
            גרסה {h.data.version} · נבדק {formatWhen(new Date(h.data.serverTime))}
          </p>
        </>
      )}
    </Section>
  );
}

// ── יומן תקלות ────────────────────────────────────────────────

const SOURCE_LABEL: Record<ErrorLogItem['source'], string> = { admin: 'מנהל', public: 'נרשם', server: 'שרת' };

function ErrorsTab() {
  const { user } = useAdmin();
  const toast = useToast();
  const [list, reload] = useLoad<{ errors: ErrorLogItem[] }>(() => ownerApi(user).errors());
  const [open, setOpen] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <Section
      title={`יומן תקלות${list.state === 'ready' ? ` (${list.data.errors.length})` : ''}`}
      aside={
        <div className="flex gap-2">
          <Button variant="secondary" className="min-h-10 px-3 text-[14px]" icon={<RefreshIcon size={16} />} onClick={reload}>
            רענון
          </Button>
          {list.state === 'ready' && list.data.errors.length > 0 && (
            <Button variant="secondary" className="min-h-10 px-3 text-[14px] text-danger" icon={<TrashIcon size={16} />} onClick={() => setConfirm(true)}>
              ניקוי
            </Button>
          )}
        </div>
      }
    >
      {confirm && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-control bg-danger-soft p-3">
          <span className="flex-1 text-[14px] font-semibold text-danger">למחוק את כל התקלות מהיומן?</span>
          <Button
            variant="danger"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await ownerApi(user).clearErrors();
                toast(`נמחקו ${r.deleted} תקלות`);
                reload();
              } catch (err) {
                toast(errorMessage(err), 'error');
              } finally {
                setBusy(false);
                setConfirm(false);
              }
            }}
          >
            מחיקה
          </Button>
          <Button variant="secondary" onClick={() => setConfirm(false)}>
            ביטול
          </Button>
        </div>
      )}
      <p className="mb-3 text-[13px] text-ink-3">
        נרשמות כאן תקלות לא צפויות: מסך שקרס אצל מנהל או נרשם, ושגיאות בשרת. שגיאות רגילות (קוד שגוי, אין אינטרנט) לא נרשמות. לא נשמרים שמות, טלפונים או תוכן טפסים.
      </p>
      {list.state === 'loading' && <Centered />}
      {list.state === 'error' && <Notice tone="error">{list.message}</Notice>}
      {list.state === 'ready' && list.data.errors.length === 0 && <p className="py-6 text-center text-[15px] text-ok">אין תקלות. 👌</p>}
      {list.state === 'ready' && list.data.errors.length > 0 && (
        <ul className="divide-y divide-line">
          {list.data.errors.map((e) => (
            <li key={e.id} className="py-2.5">
              <button type="button" onClick={() => setOpen(open === e.id ? null : e.id)} className="w-full text-start">
                <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-3">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${e.source === 'server' ? 'bg-danger-soft text-danger' : 'bg-sunken text-ink-2'}`}>{SOURCE_LABEL[e.source]}</span>
                  <span>{formatWhen(new Date(e.createdAt))}</span>
                  <span className="ltr">{e.path}</span>
                </div>
                <div className="ltr mt-1 text-start text-[14px] font-semibold text-ink">{e.message}</div>
              </button>
              {open === e.id && (
                <div className="mt-2 space-y-1">
                  {e.detail && <pre className="ltr max-h-56 overflow-auto whitespace-pre-wrap rounded-control bg-sunken p-2 text-start text-[11px]">{e.detail}</pre>}
                  <div className="ltr text-start text-[11px] text-ink-3">{e.userAgent}</div>
                  {e.uid && <div className="ltr text-start text-[11px] text-ink-3">uid: {e.uid}</div>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
